import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Copy, X, ClipboardPaste, Replace, Type } from "lucide-react";
import { toast } from "sonner";
import { lcall } from "@/lib/letters";

export const AI_ACTIONS = [
  ["decrire", "Générer depuis une description"], ["rediger", "Rédiger un courrier"], ["reponse", "Générer une réponse"],
  ["corriger", "Corriger le texte"], ["ameliorer", "Améliorer le texte"], ["reformuler", "Reformuler"],
  ["professionnel", "Plus professionnel"], ["diplomatique", "Plus diplomatique"], ["ferme", "Plus ferme"],
  ["raccourcir", "Raccourcir"], ["developper", "Développer"], ["objet", "Générer un objet"],
  ["resumer", "Résumer le courrier"], ["traduire", "Traduire"], ["niveau", "Adapter le niveau de langage"],
];
const NEEDS_TEXT = new Set(["corriger", "ameliorer", "reformuler", "professionnel", "diplomatique", "ferme", "raccourcir", "developper", "resumer", "traduire", "niveau", "reponse"]);

// Assistant IA : ne modifie jamais le document sans action explicite de l'utilisateur
export default function AiPanel({ editor, letter, onObjet, selection }) {
  const [action, setAction] = useState("decrire");
  const [prompt, setPrompt] = useState("");
  const [langue, setLangue] = useState("Anglais");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const onSel = !!selection?.text;
  const run = async () => {
    if (!editor) return;
    const text = onSel ? selection.text : (NEEDS_TEXT.has(action) || action === "objet" ? editor.getText() : "");
    setBusy(true);
    const r = await lcall(() => api.post("/letters/ai", { action, prompt, text, langue: action === "traduire" ? langue : "",
      contexte: { objet: letter.objet, destinataire: letter.destinataire, entreprise: letter.entreprise, type: letter.type, expediteur: letter.expediteur } }));
    setBusy(false);
    if (r) setRes({ ...r, sel: onSel ? selection : null });
  };
  const ins = () => {
    if (res.sel) editor.chain().focus().insertContentAt({ from: res.sel.from, to: res.sel.to }, res.text).run();
    else editor.chain().focus().insertContent(res.text).run();
    setRes(null); toast.success("Inséré dans le document");
  };
  const replaceAll = () => { if (!window.confirm("Remplacer tout le contenu du document ?")) return; editor.commands.setContent(res.text, { emitUpdate: true }); setRes(null); };
  const copy = async () => {
    const plain = res.format === "html" ? new DOMParser().parseFromString(res.text, "text/html").body.innerText : res.text;
    try { await navigator.clipboard.writeText(plain); toast.success("Copié"); } catch { toast.error("Copie impossible"); }
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-gradient-to-b from-amber-50 to-white p-3" data-testid="ai-panel">
      <p className="font-head font-semibold flex items-center gap-1.5 mb-2"><Sparkles className="h-4 w-4 text-amber-600" />Assistant IA</p>
      {onSel && <p className="text-[11px] rounded bg-amber-100 text-amber-900 px-2 py-1 mb-2" data-testid="ai-selection-hint">Sélection : « {selection.text.slice(0, 80)}{selection.text.length > 80 ? "…" : ""} »</p>}
      <select value={action} onChange={(e) => setAction(e.target.value)} className="w-full h-9 rounded-md border px-2 text-sm mb-2" data-testid="ai-action">
        {AI_ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {action === "traduire" && <input value={langue} onChange={(e) => setLangue(e.target.value)} className="w-full h-9 rounded-md border px-2 text-sm mb-2" placeholder="Langue cible" data-testid="ai-langue" />}
      <Textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-prompt"
        placeholder={action === "decrire" ? "Ex. : Je veux demander à un fournisseur de respecter le délai de livraison prévu dans notre contrat." : "Consigne complémentaire (facultatif)"} />
      <Button className="w-full mt-2 bg-amber-500 hover:bg-amber-600 text-white" onClick={run} disabled={busy} data-testid="ai-run">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{busy ? "Rédaction…" : onSel ? "Appliquer à la sélection" : "Générer"}
      </Button>
      {res && (
        <div className="mt-3 rounded-lg border bg-white" data-testid="ai-result">
          <div className="max-h-64 overflow-y-auto p-3 text-sm letter-content">
            {res.format === "html" ? <div dangerouslySetInnerHTML={{ __html: res.text }} /> : <p>{res.text}</p>}
          </div>
          <div className="grid grid-cols-2 gap-1 border-t p-2">
            {res.format === "text" && action === "objet"
              ? <Button size="sm" className="bg-brand-600 text-white col-span-2" onClick={() => { onObjet(res.text); setRes(null); }} data-testid="ai-use-objet"><Type className="h-3.5 w-3.5 mr-1" />Utiliser comme objet</Button>
              : <>
                <Button size="sm" className="bg-brand-600 text-white" onClick={ins} data-testid="ai-insert"><ClipboardPaste className="h-3.5 w-3.5 mr-1" />{res.sel ? "Remplacer la sélection" : "Insérer dans le document"}</Button>
                <Button size="sm" variant="outline" onClick={replaceAll} data-testid="ai-replace"><Replace className="h-3.5 w-3.5 mr-1" />Remplacer le contenu</Button>
              </>}
            <Button size="sm" variant="outline" onClick={copy} data-testid="ai-copy"><Copy className="h-3.5 w-3.5 mr-1" />Copier</Button>
            <Button size="sm" variant="ghost" onClick={() => setRes(null)} data-testid="ai-cancel"><X className="h-3.5 w-3.5 mr-1" />Annuler</Button>
          </div>
        </div>
      )}
    </div>
  );
}
